type RunEvent = {
  event: string;
  data: unknown;
};

type FirebaseUserLike = {
  getIdToken: () => Promise<string>;
};

type FirebaseAuthLike = {
  currentUser?: FirebaseUserLike | null;
};

function extractErrorMessage(payload: unknown): string | null {
  if (typeof payload === "string" && payload.trim().length > 0) {
    return payload.trim();
  }
  if (!payload || typeof payload !== "object") return null;
  const record = payload as Record<string, unknown>;
  if (typeof record.message === "string" && record.message.trim().length > 0) {
    return record.message.trim();
  }
  if (typeof record.error === "string" && record.error.trim().length > 0) {
    return record.error.trim();
  }
  return null;
}

export async function runGraphSSE(opts: {
  endpoint?: string;
  input: string;
  userId: string;
  clientId?: string;
  threadId?: string;
  language?: string;
  task?: string;
  previousContent?: string;
  clarificationsText?: string;
  meta?: Record<string, unknown>;
  kind?: 'message' | 'clarification' | 'clarification_skip' | 'clarification_response' | 'artifact_feedback_response';
  type?: string;
  resume?: boolean;
  resumeFromRunId?: string;
  signal?: AbortSignal;
  onEvent?: (e: RunEvent) => void;
  // PHASE-02 START
  onDone?: (payload: unknown) => void;
  // PHASE-02 END
}): Promise<unknown> {
  const endpoint = opts.endpoint || process.env.NEXT_PUBLIC_GRAPH_ENDPOINT || "/api/graph/run";
  // Try to attach Firebase ID token for server-side verification
  let authHeader: Record<string, string> = {};
  try {
    // Lazy import to avoid SSR issues
    const { auth } = await import('@/lib/firebase');
    const user = (auth as FirebaseAuthLike)?.currentUser;
    const token = user && (await user.getIdToken());
    if (token) authHeader = { Authorization: `Bearer ${token}` };
  } catch {}

  const res = await fetch(endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      ...authHeader,
    },
    body: JSON.stringify({
      input: opts.input,
      userId: opts.userId,
      ...(opts.clientId ? { clientId: opts.clientId } : {}),
      ...(opts.threadId ? { threadId: opts.threadId } : {}),
      ...(opts.language ? { language: opts.language } : {}),
      ...(opts.task ? { task: opts.task } : {}),
      ...(opts.previousContent ? { previousContent: opts.previousContent } : {}),
      ...(opts.clarificationsText ? { clarificationsText: opts.clarificationsText } : {}),
      ...(opts.meta ? { meta: opts.meta } : {}),
      ...(opts.kind ? { kind: opts.kind } : {}),
      ...(opts.type ? { type: opts.type } : {}),
      ...(typeof opts.resume === 'boolean' ? { resume: opts.resume } : {}),
      ...(opts.resumeFromRunId ? { resumeFromRunId: opts.resumeFromRunId } : {}),
    }),
    signal: opts.signal,
  });

  if (!res.ok || !res.body) {
    throw new Error(`Graph request failed: ${res.status}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let finalData: unknown = null;
  let sawTerminal = false;
  let terminalError: string | null = null;

  while (true) {
    const { value, done } = await reader.read();
    if (value && value.length) {
      buffer += decoder.decode(value, { stream: !done });
    }
    if (buffer.includes("\r\n")) {
      buffer = buffer.replace(/\r\n/g, "\n");
    }
    if (done) {
      const trailing = decoder.decode();
      if (trailing) buffer += trailing;
      if (buffer.trim()) {
        buffer = `${buffer}\n\n`;
      }
    }

    let sepIndex: number;
    while ((sepIndex = buffer.indexOf("\n\n")) !== -1) {
      const rawEvent = buffer.slice(0, sepIndex);
      buffer = buffer.slice(sepIndex + 2);

      const lines = rawEvent.split("\n");
      let eventName = "message";
      const dataLines: string[] = [];
      for (const line of lines) {
        if (line.startsWith("event:")) eventName = line.slice(6).trim();
        if (line.startsWith("data:")) dataLines.push(line.slice(5).trimStart());
      }

      const dataLine = dataLines.join("\n").trim();
      let data: unknown = null;
      try {
        data = dataLine ? JSON.parse(dataLine) : null;
      } catch {
        data = dataLine;
      }

      opts.onEvent?.({ event: eventName, data });

      if (eventName === "error") {
        sawTerminal = true;
        terminalError = extractErrorMessage(data) || "Graph stream error";
        break;
      }

      if (eventName === "done") {
        sawTerminal = true;
        finalData = data;
        // PHASE-02 START
        opts.onDone?.(data);
        // PHASE-02 END
      }
    }

    if (sawTerminal) break;
    if (done) break;
  }

  if (terminalError) {
    throw new Error(terminalError);
  }
  if (!sawTerminal) {
    throw new Error("Graph stream ended without terminal event");
  }

  return finalData;
}
